import { createI18n } from 'vue-i18n'
import config from '../config'
import pluralizationRules from './plural.js'
import request from '@/helpers/request'
import Sentry from '@/modules/Sentry'
import { SUPPORTED_LANGUAGES } from '@/helpers/constants'
import { dateProvider } from '@/helpers/date'
import { useLocationStore } from '@/store/location'
import { useI18nStore } from '@/store/i18n'
import { useStatusStore } from '@/store/status'

/**
 * vue-i18n v9 in legacy mode.
 *
 * TECH DEBT:
 * 1. $tc() deprecated in v9 (16 usages). Works in legacy mode, removed in v10.
 *    Fix: $tc('key', count) → $t('key', count). CAUTION: broke some translations
 *    when attempted — test per-locale before migrating.
 * 2. <i18n path=""> renamed to <i18n-t keypath=""> in v9. All 26 usages migrated.
 * 3. $te() deprecated in v9.2+ composition mode (1 usage). Works in legacy.
 * 4. legacy:true keeps Vue 2 API. To migrate to composition: set legacy:false,
 *    update all components to useI18n() composable.
 */

/**
 * Escape literal `@` in translation messages that are NOT part of vue-i18n linked message syntax.
 *
 * vue-i18n v9 message compiler treats `@` as the start of a linked message (`@:key` / `@.modifier:key`).
 * Translation strings containing email addresses like `support@simple.life` break with "Invalid linked format".
 * This was not an issue in vue-i18n v8 (Vue 2).
 *
 * The regex matches `@` preceded by a word character and followed by a word character (email-like pattern)
 * and replaces it with the vue-i18n v9 literal escape syntax `{'@'}`.
 */
export function escapeLinkedFormatInMessages(obj) {
	if (typeof obj === 'string') {
		return obj.replace(/(\w)@(\w)/g, "$1{'@'}$2").replace(/%\{/g, "{'%'}{")
	}
	if (typeof obj === 'object' && obj !== null) {
		for (const key in obj) {
			obj[key] = escapeLinkedFormatInMessages(obj[key])
		}
	}
	return obj
}
export const i18n = createI18n({
	legacy: true,
	locale: '', // set locale
	fallbackLocale: 'en',
	messages: {},
	pluralizationRules,
	warnHtmlInMessage: 'off',
	fallbackWarn: false,
	missingWarn: false,
})

export async function loadLanguageAsync({ i18nLocaleOverride = null } = {}) {
	const locationStore = useLocationStore()
	const queryParamLang = locationStore.getQueryParam('lang')?.toLocaleLowerCase()
	const queryParamLpId = locationStore.getQueryParam('lpid')

	if (queryParamLang && SUPPORTED_LANGUAGES.includes(queryParamLang)) {
		window.language = queryParamLang
	}

	const lang = window.language
	document.documentElement.setAttribute('lang', lang)
	document.documentElement.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr')

	if (i18n.global.locale === lang && !i18nLocaleOverride) {
		return
	}

	try {
		const response = await request(`${config('ApiLocalization')}${i18nLocaleOverride || lang}/web.json`)

		i18n.global.setLocaleMessage(lang, escapeLinkedFormatInMessages(response))

		i18n.global.locale = lang
		const i18nStore = useI18nStore()
		i18nStore.setLanguage(lang)

		dateProvider.loadLocaleAsync()

		if (queryParamLpId) {
			await i18nStore.loadCustomLandingData()
		}

		const statusStore = useStatusStore()
		statusStore.setResourceReadiness('i18n')

		document.title = i18n.global.t('common.main_pagetitle')
	} catch (e) {
		if (e.name !== 'AxiosError') {
			Sentry.captureException(e)
		}
	}
}
